/* eslint-disable @typescript-eslint/naming-convention */
/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { ContentTypeSetter } from '../../services/navigation/utils/content-type-setter';
import { PageStateContext } from '../../services';

type SEOProps = {
  pageType?: string;
  title?: string;
  description?: string;
  lang?: string;
  og_image?: string;
  og_title?: string;
  og_description?: string;
  twitter_image?: string;
  twitter_title?: string;
  twitter_description?: string;
  slug?: string;
  authorized_roles: string[];
  robots: string;
  cludo: boolean;
  teaser_image?: string;
  teaser_title?: string;
  first_published_at?: string;
};

export function SEO({
  pageType,
  title,
  description,
  lang,
  robots,
  og_image,
  og_title,
  og_description,
  twitter_image,
  twitter_title,
  twitter_description,
  slug,
  authorized_roles,
  cludo,
  teaser_image,
  teaser_title,
  first_published_at,
}: SEOProps): JSX.Element {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            author
            url
            defaultLanguage
          }
        }
      }
    `,
  );
  const isPrivate = (authorized_roles !== undefined) && (authorized_roles.includes('authorized')) ? 'yes' : 'no';
  const { environment, settings } = useContext(PageStateContext);

  const isProfessionalPressRelease = pageType === 'page-press-news' && authorized_roles?.length > 0;

  return (
    <Helmet
      htmlAttributes={{
        lang: lang === 'default' ? site.siteMetadata.defaultLanguage : lang,
      }}
      title={title}
      meta={[
        {
          name: 'description',
          content: isProfessionalPressRelease
            ? settings.protected_press_articles_text : description,
        },
        {
          property: 'og:image',
          content: og_image,
        },
        {
          property: 'og:title',
          content: og_title,
        },
        {
          property: 'og:description',
          content: isProfessionalPressRelease
            ? settings.protected_press_articles_text : og_description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:url',
          content: `${site.siteMetadata.url}/${slug}`,
        },
        {
          name: 'twitter:image',
          content: twitter_image,
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:creator',
          content: environment.twitterHandle,
        },
        {
          name: 'twitter:title',
          content: twitter_title,
        },
        {
          name: 'twitter:description',
          content: twitter_description,
        },
        {
          name: 'private',
          content: isPrivate,
        },
        {
          name: 'robots',
          content: robots,
        },
        {
          name: 'content-type',
          content: ContentTypeSetter(slug),
        },
        {
          name: 'image',
          content: teaser_image,
        },
        {
          name: 'title',
          content: teaser_title,
        },
        {
          name: 'published-date',
          content: first_published_at,
        },
        ((cludo && { name: 'clubot', content: 'noindex,nofollow' }) || {}),
      ]}
    />
  );
}

SEO.defaultProps = {
  lang: 'en',
  title: '',
  description: '',
  og_image: '',
  og_title: '',
  og_description: '',
  twitter_image: '',
  twitter_title: '',
  twitter_description: '',
  slug: '',
  robots: '',
};
